import React, { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../store";
import KLCollapsibleSection from "../KLCollapsibleSection";
import KLTrendingUpIcon from "../icons/KLTrendingUpIcon";
import KLEmptyState from "../KLEmptyState";
import { EmptyChart } from "../KLEmptyState/illustrations";
import { MonthsContainer, MonthButton, NoDataMessage } from "./styles";

interface KLDashboardInsightsProps {
  businessId: string;
}

const KLDashboardInsights: React.FC<KLDashboardInsightsProps> = ({
  businessId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const { availablePeriods, loadingPeriods } = useSelector(
    (state: RootState) => state.insights,
  );

  const availableMonths = useMemo(() => {
    return availablePeriods
      .flatMap((period) =>
        period.months.map((month) => ({
          year: period.year,
          month,
          label: DateTime.fromObject({ year: period.year, month })
            .setLocale("he")
            .toFormat("MMMM yyyy"),
        })),
      )
      .sort((a, b) => {
        // Sort by most recent first
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
      });
  }, [availablePeriods]);

  const handleMonthClick = useCallback(
    (year: number, month: number) => {
      navigate(`/insights/${businessId}/${year}/${month}`);
    },
    // OME-209: Suppressing to preserve existing behavior; see ticket for rationale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [businessId, dispatch, navigate],
  );

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <KLCollapsibleSection
      title="הנתונים החודשיים שלי"
      isExpanded={isExpanded}
      onToggle={handleToggle}
      icon={<KLTrendingUpIcon size="small" customStyle={false} withGlow={false} />}
      variant="red"
    >
      {loadingPeriods ? (
        <NoDataMessage>טוען תקופות זמינות...</NoDataMessage>
      ) : availableMonths.length > 0 ? (
        <MonthsContainer>
          {availableMonths.map(({ year, month, label }) => (
            <MonthButton
              key={`${year}-${month}`}
              onClick={() => handleMonthClick(year, month)}
            >
              {label}
            </MonthButton>
          ))}
        </MonthsContainer>
      ) : (
        <KLEmptyState
          illustration={<EmptyChart />}
          title="אין עדיין נתונים זמינים לניתוח"
          description="הנתונים יהיו זמינים לאחר שיתקבלו הזמנות ראשונות. המשך להציע שירותים מעולים ללקוחות שלך!"
          size="small"
        />
      )}
    </KLCollapsibleSection>
  );
};

export default KLDashboardInsights;
